// eslint-disable-next-line vue/prefer-import-from-vue
import { hyphenate, camelize } from '@vue/shared'
import {
  onClickOutside as _onClickOutside,
  type MaybeElementRef,
  type OnClickOutsideHandler,
  type OnClickOutsideOptions
} from '@vueuse/core'
import { parsePhoneNumber } from 'awesome-phonenumber'
import { v4 as uuidv4 } from 'uuid'
import { getCurrentInstance, toHandlerKey } from 'vue'
import { type ISODateTimeString, isISODateTimeString } from '@gat-ship/types'
import { Temporal, Intl } from '@js-temporal/polyfill'
import {
  formatToDateTimeString,
  formatToTimeString,
  formatToShortDate
} from './global-date-functions'

/** @param obj Accepts a object of type T,
 * @returns a duplicate object with no reference to the object sent in to the param.  */
export function duplicateViaJson<T>(obj: T): T {
  if (obj) {
    const jsonStr = JSON.stringify(obj)
    return JSON.parse(jsonStr)
  }
  return obj
}

export function pascalToCamelCase(str: string): string {
  if (!str) return str
  return str.charAt(0).toLowerCase() + str.slice(1)
}

function formatDate(
  date: ISODateTimeString,
  format?: 'short' | 'full' | 'long' | 'medium'
): string {
  if (!isISODateTimeString(date)) {
    return date
  }
  return Temporal.Instant.from(date).toLocaleString(
    Intl.DateTimeFormat().resolvedOptions().locale,
    {
      dateStyle: format ?? 'short'
    }
  )
}

function catchError<T, U>(promsie: Promise<T>): Promise<[undefined, T] | [U]> {
  return promsie
    .then((data) => {
      return [undefined, data] as [undefined, T]
    })
    .catch((error) => {
      return [error] as [U]
    })
}

function onClickOutisde<T extends OnClickOutsideOptions>(
  target: MaybeElementRef,
  handler: OnClickOutsideHandler<T>,
  options?: T
) {
  return _onClickOutside<T>(target, handler, options)
}

function generateUuid() {
  return uuidv4()
}

// Function to generate a short unique ID starting with 'input-'
function generateShortUniqueId(prefix?: string) {
  const timestamp = Date.now().toString(36) // Convert timestamp to base-36 string
  const randomNum = Math.random().toString(36).substring(2, 8) // Generate a random base-36 string
  return `${prefix ? prefix + '-' : ''}${timestamp}-${randomNum}`
}

/**
 * Determines if the current component has a listener for a specific event
 *
 * See https://github.com/vuejs/core/blob/main/packages/runtime-core/src/componentEmits.ts#L166
 *
 * @param event Name of the event you wish to look for, eg: 'click'
 */
export function hasEmitListener(event: string) {
  const instance = getCurrentInstance()
  const props = instance?.vnode.props
  const isModelListener = event.startsWith('update:')
  if (!props) return false

  let handler =
    props[toHandlerKey(event)] ||
    // also try camelCase event handler (#2249)
    props[toHandlerKey(camelize(event))]

  if (!handler && isModelListener) {
    handler = props[toHandlerKey(hyphenate(event))]
  }

  return Boolean(handler)
}

function isIdenticalObjects(objA: unknown, objB: unknown): boolean {
  let result = false
  const v1 = JSON.stringify(objA)
  const v2 = JSON.stringify(objB)
  result = v1 === v2
  return result
}

function formatPhoneNumber(value: string | undefined | null) {
  let result = value
  if (result) {
    const parsed = parsePhoneNumber(result)
    if (parsed.valid) {
      return parsed.number
    }
  }
  if (!result) {
    result = undefined
  }
  return result
}

export type ToFormatFormats = 'phone-number' | 'date' | 'date-time' | 'time'

export type ToFormatOptions<T extends ToFormatFormats> = T extends 'date-time'
  ? {
      format: 'short' | 'long' | 'medium' | 'full'
      options?: {
        hideYearIfCurrent?: boolean
      }
    }
  : undefined

export type ReturnToFormat<T extends ToFormatFormats> = T extends 'phone-number'
  ? string
  : string

/**
 * Formats a string, date or undefined value to a specific format.
 * @param value The value to format.
 * @param format The format to format the value to.
 * @param formatOptions Optional format options.
 * @returns The formatted value or the original value if it is undefined.
 */
export function formatString<T extends ToFormatFormats>(
  value: string | undefined,
  format: T,
  formatOptions?: ToFormatOptions<T>
): ReturnToFormat<T> {
  if (!value) {
    return value as ReturnToFormat<T>
  }

  if (format === 'phone-number' && typeof value === 'string') {
    const parsedPhoneNumber = parsePhoneNumber(value)
    if (parsedPhoneNumber.valid) {
      const type = 'international'
      const phoneNo = parsedPhoneNumber.number[type]
      if (phoneNo) {
        return phoneNo
      }
    }
    return value
  }

  if (format === 'date') {
    const date = formatToShortDate(value as ISODateTimeString)
    return date as ReturnToFormat<T>
  }
  if (format === 'date-time') {
    if (!isISODateTimeString(value)) {
      return value
    }
    const date = formatToDateTimeString(
      value as ISODateTimeString,
      formatOptions?.format,
      formatOptions?.options
    )
    return date as ReturnToFormat<T>
  }
  if (format === 'time') {
    const time = formatToTimeString(value as ISODateTimeString)
    return time as ReturnToFormat<T>
  }

  return value as ReturnToFormat<T>
}

export function toPlural(
  text: string | undefined,
  count: number,
  pluralText: string = 's',
  removeLastLetter: boolean = false
) {
  if (!text) {
    return text
  }
  if (removeLastLetter) {
    text = text.slice(0, -1)
  }
  return count === 1 ? text : `${text}${pluralText}`
}

export type TimeRemainingResult<T extends boolean> = T extends true
  ? string
  : {
      years: number
      months: number
      days: number
      hours: number
      minutes: number
      seconds: number
    }

function getTimeRemainingFromNow<T extends boolean = true>(
  date: Date | string,
  humanReadable: T = true as T,
  format?: T extends true
    ? {
        years?: string
        months?: string
        days?: string
        hours?: string
        minutes?: string
        seconds?: string
      }
    : never
): TimeRemainingResult<T> {
  const now = Temporal.Now.zonedDateTimeISO(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  )
  const past =
    typeof date === 'string'
      ? Temporal.Instant.from(date).toZonedDateTimeISO(
          Intl.DateTimeFormat().resolvedOptions().timeZone
        )
      : Temporal.Instant.from(date.toISOString()).toZonedDateTimeISO(
          Intl.DateTimeFormat().resolvedOptions().timeZone
        )
  const diff = now.since(past, { largestUnit: 'years' })

  const getPositiveValuesAsZero = (value: number) => {
    const newValue = Math.floor(value)
    if (newValue === undefined) {
      return 0
    }
    if (newValue >= 0) {
      return 0
    }
    return Math.abs(newValue)
  }

  if (humanReadable) {
    const years = diff.years
      ? `${getPositiveValuesAsZero(diff.years)} ${format?.years ?? 'years'}`
      : undefined
    const months = diff.months
      ? `${getPositiveValuesAsZero(diff.months)} ${format?.months ?? 'months'}`
      : undefined
    const days = diff.days
      ? `${getPositiveValuesAsZero(diff.days)} ${format?.days ?? 'days'}`
      : undefined
    const hours = diff.hours
      ? `${getPositiveValuesAsZero(diff.hours)} ${format?.hours ?? 'hours'}`
      : undefined
    const minutes = diff.minutes
      ? `${getPositiveValuesAsZero(diff.minutes)} ${format?.minutes ?? 'minutes'}`
      : undefined
    const seconds = diff.seconds
      ? `${getPositiveValuesAsZero(diff.seconds)} ${format?.seconds ?? 'seconds'}`
      : undefined
    if (years) {
      return `${years} ${months} ${days} ${hours} ${minutes} ${seconds}` as TimeRemainingResult<T>
    }
    if (months) {
      return `${months} ${days} ${hours} ${minutes} ${seconds}` as TimeRemainingResult<T>
    }
    if (days) {
      return `${days} ${hours} ${minutes} ${seconds}` as TimeRemainingResult<T>
    }
    if (hours) {
      return `${hours} ${minutes} ${seconds}` as TimeRemainingResult<T>
    }
    if (minutes) {
      return `${minutes} ${seconds}` as TimeRemainingResult<T>
    }
    if (seconds) {
      return `${seconds}` as TimeRemainingResult<T>
    }
    return 'Just now' as TimeRemainingResult<T>
  }
  return {
    years: getPositiveValuesAsZero(diff.years ?? 0),
    months: getPositiveValuesAsZero(diff.months ?? 0),
    days: getPositiveValuesAsZero(diff.days ?? 0),
    hours: getPositiveValuesAsZero(diff.hours ?? 0),
    minutes: getPositiveValuesAsZero(diff.minutes ?? 0),
    seconds: getPositiveValuesAsZero(diff.seconds ?? 0)
  } as TimeRemainingResult<T>
}

function dateNow(
  hours?: number,
  minutes?: number,
  seconds?: number,
  timeZone?: string
): ISODateTimeString {
  const now = Temporal.Now.zonedDateTimeISO(
    timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
  ).with({
    hour: hours ?? undefined,
    minute: minutes ?? undefined,
    second: seconds ?? undefined,
    millisecond: 0,
    nanosecond: 0,
    microsecond: 0
  })

  return now.toInstant().toString() as ISODateTimeString
}

function fromNow(options?: {
  days?: number
  months?: number
  years?: number
  hours?: number
  minutes?: number
  seconds?: number
  timeZone?: string
}) {
  const { days, months, years, hours, minutes, seconds, timeZone } =
    options ?? {}

  // If no duration parameters are provided, return current time
  if (days === undefined && months === undefined && years === undefined) {
    const now = Temporal.Now.zonedDateTimeISO(
      timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
    ).with({
      hour: hours ?? undefined,
      minute: minutes ?? undefined,
      second: seconds ?? undefined,
      millisecond: 0,
      nanosecond: 0
    })

    const result = {
      utc: now.toInstant().toString() as ISODateTimeString,
      local: now
        .toInstant()
        .toLocaleString(
          Intl.DateTimeFormat().resolvedOptions().locale
        ) as ISODateTimeString
    }

    return Object.assign(result.utc, {
      local: result.local
    }) as ISODateTimeString
  }

  const now = Temporal.Now.zonedDateTimeISO(
    timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
  )
    .with({
      hour: hours ?? undefined,
      minute: minutes ?? undefined,
      second: seconds ?? undefined,
      millisecond: 0,
      nanosecond: 0
    })
    .add({
      days: days ?? undefined,
      months: months ?? undefined,
      years: years ?? undefined
    })

  const result = {
    utc: now.toInstant().toString() as ISODateTimeString,
    local: now
      .toInstant()
      .toLocaleString(
        Intl.DateTimeFormat().resolvedOptions().locale
      ) as ISODateTimeString
  }

  return Object.assign(result.utc, { local: result.local }) as ISODateTimeString
}

function dateForever() {
  return '2099-12-31T00:00:00Z' as ISODateTimeString
}

export function arrayToExcelFormat(
  data: any[],
  options: {
    headers: string[] // Make headers required
    delimiter?: string
    includeHeaders?: boolean
  }
): string {
  if (!data || data.length === 0) return ''

  const {
    headers,
    delimiter = '\t', // Tab delimiter for Excel compatibility
    includeHeaders = true
  } = options

  // Validate that headers are provided
  if (!headers || headers.length === 0) {
    throw new Error('Headers are required for data sanitization')
  }

  let csvContent = ''

  // Add headers if requested
  if (includeHeaders && headers.length > 0) {
    csvContent += headers.join(delimiter) + '\n'
  }

  // Add data rows
  for (const row of data) {
    const rowValues = headers.map((header) => {
      const value = row[header]
      if (value === null || value === undefined) return ''

      // Convert value to string and handle special cases
      let stringValue = String(value)

      // Escape quotes and wrap in quotes if contains delimiter, quotes, or newlines
      if (
        stringValue.includes(delimiter) ||
        stringValue.includes('"') ||
        stringValue.includes('\n')
      ) {
        stringValue = `"${stringValue.replace(/"/g, '""')}"`
      }

      return stringValue
    })

    csvContent += rowValues.join(delimiter) + '\n'
  }

  return csvContent.trim()
}

export function copyArrayToExcelClipboard(
  data: any[],
  options: {
    headers: string[] // Make headers required
    delimiter?: string
    includeHeaders?: boolean
    showNotification?: boolean
  }
): Promise<boolean> {
  try {
    const excelContent = arrayToExcelFormat(data, options)

    if (!excelContent) {
      console.warn('No data to copy to clipboard')
      return Promise.resolve(false)
    }

    return navigator.clipboard.writeText(excelContent).then(() => {
      if (options?.showNotification !== false) {
        // You can integrate this with your notification system
        console.log('Data copied to clipboard in Excel format')
      }
      return true
    })
  } catch (error) {
    console.error('Failed to copy data to clipboard:', error)
    return Promise.resolve(false)
  }
}

/** A simple function to copy text to the clipboard. No formatting or validation. */
function copyToClipboard(text: string) {
  return navigator.clipboard.writeText(text)
}

export type ElementPosition = {
  left: number
  right: number
  top: number
  bottom: number
  absoluteLeft: number
  absoluteRight: number
  absoluteTop: number
  absoluteBottom: number
}

/**
 * Get the position of an element
 * @param el The element to get the position of
 * @returns The position of the element
 */
function getElementPosition(el: HTMLElement | null): ElementPosition {
  if (el) {
    const rect = el.getBoundingClientRect()
    return {
      left: rect.left,
      right: rect.right,
      top: rect.top,
      bottom: rect.bottom,
      absoluteLeft: rect.left + window.scrollX,
      absoluteRight: rect.right + window.scrollX,
      absoluteTop: rect.top + window.scrollY,
      absoluteBottom: rect.bottom + window.scrollY
    }
  }
  return {
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    absoluteLeft: 0,
    absoluteRight: 0,
    absoluteTop: 0,
    absoluteBottom: 0
  }
}

export const globalHelperFunctions = {
  getTimeRemainingFromNow,
  duplicateViaJson,
  pascalToCamelCase,
  formatDate,
  formatPhoneNumber,
  formatString,
  toPlural,
  catchError,
  onClickOutisde,
  uuid: generateUuid,
  generateShortUniqueId,
  isIdenticalObjects,
  dateNow,
  dateForever,
  fromNow,
  arrayToExcelFormat,
  copyArrayToExcelClipboard,
  copyToClipboard,
  getElementPosition,
  components: {
    hasEmitListener
  }
}
